/**
 * Sprint-104d-full — LiveTrackingMap.
 *
 * Renders pickup pin, drop pin, route polyline, and fits bounds on mount.
 * Reserves a slot for an `agent` marker — Phase 105-Agent-C will populate
 * it via the customer-tracking poll. For Sprint-104d we just render the
 * pickup/drop view.
 *
 * Design notes:
 *   • Uses the `mapProvider` façade — NEVER imports react-leaflet directly.
 *   • Polyline fetched once from OSRM (`router.project-osrm.org`). If the
 *     fetch fails we fall back to a straight line so the map never looks
 *     broken.
 *   • Fits bounds on mount + on every coords change.
 *   • Map size honours the parent — defaults to 280px tall, 360px on sm+.
 */
import { useEffect, useState } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Polyline,
  useMap,
  createDotIcon,
  DEFAULT_TILE_URL,
  DEFAULT_TILE_ATTRIBUTION,
  L,
} from "./mapProvider";

const ICON_PICKUP   = createDotIcon("#10B981", 18); // emerald-500
const ICON_DROP     = createDotIcon("#FF6B00", 18); // PickNGo orange
const ICON_AGENT    = createDotIcon("#3B82F6", 20); // blue-500 (Phase-C ready)

function FitBounds({ points }) {
  const map = useMap();
  const key = JSON.stringify(points);
  useEffect(() => {
    if (!points || points.length < 1) return;
    if (points.length === 1) {
      map.setView(points[0], 13);
      return;
    }
    const bounds = L.latLngBounds(points);
    map.fitBounds(bounds, { padding: [40, 40] });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, map]);
  return null;
}

export default function LiveTrackingMap({ pickup, delivery, agent }) {
  const [routeCoords, setRouteCoords] = useState([]);
  const [routeError, setRouteError]   = useState(false);

  // OSRM lookup. We deliberately don't await this on the render path —
  // the straight-line polyline shows immediately while the road geometry
  // streams in over the next 500-1500 ms.
  useEffect(() => {
    if (!pickup?.lat || !delivery?.lat) return;
    let cancelled = false;
    fetch(
      `https://router.project-osrm.org/route/v1/driving/${pickup.lng},${pickup.lat};${delivery.lng},${delivery.lat}?overview=full&geometries=geojson`,
    )
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        if (data.code === "Ok" && data.routes?.[0]) {
          setRouteCoords(data.routes[0].geometry.coordinates.map(([lng, lat]) => [lat, lng]));
        } else {
          setRouteError(true);
        }
      })
      .catch(() => { if (!cancelled) setRouteError(true); });
    return () => { cancelled = true; };
  }, [pickup?.lat, pickup?.lng, delivery?.lat, delivery?.lng]);

  if (!pickup?.lat || !delivery?.lat) {
    return (
      <div
        className="w-full h-[280px] sm:h-[360px] rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 flex items-center justify-center text-sm text-slate-500"
        data-testid="live-tracking-map-skeleton"
      >
        Map will appear once pickup &amp; delivery coordinates resolve.
      </div>
    );
  }

  const points = [
    [pickup.lat, pickup.lng],
    [delivery.lat, delivery.lng],
    ...(agent?.lat && agent?.lng ? [[agent.lat, agent.lng]] : []),
  ];
  // Straight-line fallback so the map never looks empty.
  const straightLine = [[pickup.lat, pickup.lng], [delivery.lat, delivery.lng]];

  return (
    <div className="w-full h-[280px] sm:h-[360px] rounded-2xl overflow-hidden border border-slate-200 shadow-sm relative" data-testid="live-tracking-map">
      {agent?.lat && agent?.lng && agent?.stale && (
        <div
          className="absolute top-3 left-3 z-[1000] inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-100 border border-amber-300 text-amber-900 text-[11px] font-semibold shadow-sm"
          data-testid="agent-pin-stale-badge"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
          Location lagging · last update &gt;60s ago
        </div>
      )}
      <MapContainer
        center={[(pickup.lat + delivery.lat) / 2, (pickup.lng + delivery.lng) / 2]}
        zoom={6}
        scrollWheelZoom={false}
        style={{ height: "100%", width: "100%" }}
      >
        <TileLayer url={DEFAULT_TILE_URL} attribution={DEFAULT_TILE_ATTRIBUTION} />
        <FitBounds points={points} />
        <Marker position={[pickup.lat, pickup.lng]} icon={ICON_PICKUP} />
        <Marker position={[delivery.lat, delivery.lng]} icon={ICON_DROP} />
        {agent?.lat && agent?.lng && (
          <Marker
            position={[agent.lat, agent.lng]}
            icon={ICON_AGENT}
            opacity={agent.stale ? 0.55 : 1}
          />
        )}
        <Polyline
          positions={routeCoords.length > 0 ? routeCoords : straightLine}
          pathOptions={{
            color: routeError && routeCoords.length === 0 ? "#94a3b8" : "#FF6B00",
            weight: 3.5,
            opacity: 0.85,
            dashArray: routeError && routeCoords.length === 0 ? "6, 8" : undefined,
          }}
        />
      </MapContainer>
    </div>
  );
}
